//#region import libraries
import React, { useEffect, useState } from 'react';

// atoms
import { Accordion } from '_atoms';

// bootstrap
import { Col, Form, InputGroup, ListGroup, Row } from 'react-bootstrap'

// styling
import { Colors } from '_styles';

//icons
import { IoMdClose } from 'react-icons/io';
import { MdClear } from "react-icons/md";
import { FiPlus } from "react-icons/fi";

// question types (single source of truth)
import { QUESTION_TYPES, typeNeedsOptions } from './survey-question-types';

//#endregion



const ensureOptions = (q, lang) => {
    if (!q.options || typeof q.options !== "object") q.options = {};
    if (!Array.isArray(q.options[lang])) q.options[lang] = [];
};

const QuestionForm = ({ questions, setQuestions, selectedLanguages, handleDeleteQuestion }) => {

    const [option, setOption] = useState({}); // Input value for the new option

    // Initialize option inputs for selected languages
    useEffect(() => {
        const initialOption = {};
        selectedLanguages.forEach((lang) => {
            initialOption[lang.value] = "";
        });
        setOption(initialOption);
    }, [selectedLanguages]);

    const updateQuestion = (e, index, lang) => {
        const { name, value } = e.target;
        const updatedQuestions = [...questions];

        if (name === "text") {
            updatedQuestions[index].text[lang] = value;
        } else {
            updatedQuestions[index][name] = value;
        }

        setQuestions(updatedQuestions);
    };

    // add option
    const handleAddOption = (index, lang) => {
        const value = option[lang]?.trim();
        if (!value) return;

        const updatedQuestions = [...questions];
        // defensive: ensure object/array exist
        ensureOptions(updatedQuestions[index], lang);

        // avoid mutating nested arrays in place (safer for React)
        updatedQuestions[index].options[lang] = [
            ...updatedQuestions[index].options[lang],
            value,
        ];

        setQuestions(updatedQuestions);
        setOption((prev) => ({ ...prev, [lang]: "" }));
    };

    // edit option
    const handleEditOption = (questionIndex, lang, optionIndex, value) => {
        const updatedQuestions = [...questions];
        ensureOptions(updatedQuestions[questionIndex], lang);

        // make a copy of array before modifying
        const arr = [...updatedQuestions[questionIndex].options[lang]];
        arr[optionIndex] = value;
        updatedQuestions[questionIndex].options[lang] = arr;

        setQuestions(updatedQuestions);
    };

    // delete option
    const handleDeleteOption = (questionIndex, lang, optionIndex) => {
        const updatedQuestions = [...questions];
        ensureOptions(updatedQuestions[questionIndex], lang);

        const arr = [...updatedQuestions[questionIndex].options[lang]];
        arr.splice(optionIndex, 1);
        updatedQuestions[questionIndex].options[lang] = arr;

        setQuestions(updatedQuestions);
    };

    //#region matrix
    // helper to ensure items exists
    const ensureItems = (q) => {
        if (!Array.isArray(q.items)) q.items = [];
    };

    // helper to ensure scaleLabels exists with 5 empty strings per language
    const ensureScaleLabels = (q, langs) => {
        if (!q.scaleLabels || typeof q.scaleLabels !== "object") q.scaleLabels = {};
        langs.forEach(lang => {
            if (!Array.isArray(q.scaleLabels[lang.value])) {
                q.scaleLabels[lang.value] = ["", "", "", "", ""];
            }
        });
    };

    // Edit scale label
    const handleEditScaleLabel = (questionIndex, lang, labelIndex, value) => {
        const updated = [...questions];
        ensureScaleLabels(updated[questionIndex], selectedLanguages);

        const labelsCopy = [...updated[questionIndex].scaleLabels[lang]];
        labelsCopy[labelIndex] = value;
        updated[questionIndex].scaleLabels[lang] = labelsCopy;
        setQuestions(updated);
    };

    // Add new empty item for all languages
    const handleAddMatrixItem = (questionIndex) => {
        const updated = [...questions];
        ensureItems(updated[questionIndex]);

        // build default localized text for new item
        const newItem = { text: {} };
        selectedLanguages.forEach(l => newItem.text[l.value] = '');

        updated[questionIndex].items = [...updated[questionIndex].items, newItem];
        setQuestions(updated);
    };

    // Edit matrix item localized text
    const handleEditMatrixItem = (questionIndex, itemIndex, lang, value) => {
        const updated = [...questions];
        ensureItems(updated[questionIndex]);

        const itemsCopy = [...updated[questionIndex].items];
        const itemCopy = { ...itemsCopy[itemIndex], text: { ...itemsCopy[itemIndex].text, [lang]: value } };
        itemsCopy[itemIndex] = itemCopy;
        updated[questionIndex].items = itemsCopy;
        setQuestions(updated);
    };

    // Remove an item
    const handleDeleteMatrixItem = (questionIndex, itemIndex) => {
        const updated = [...questions];
        ensureItems(updated[questionIndex]);
        const itemsCopy = [...updated[questionIndex].items];
        itemsCopy.splice(itemIndex, 1);
        updated[questionIndex].items = itemsCopy;
        setQuestions(updated);
    };

    //#endregion

    return (
        <div>
            {questions.map((question, index, fullQuestionsArr) => (
                <Row key={index}>
                    <Col md={11}>
                        <Accordion header={`Question ${index + 1}`} active={index === questions.length - 1} onStateChange={(state) => {
                            console.log("state changed");
                        }}>
                            <Row className="mt-4" style={{ padding: 4 }}>
                                {/* Question text fields for each selected language */}
                                {selectedLanguages.map((lang) => (
                                    <Col md={4} key={lang.value}>
                                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                                            Question Text ({lang.label})
                                        </Form.Label>
                                        <Form.Control
                                            onChange={(e) => updateQuestion(e, index, lang.value)}
                                            type="text-area"
                                            name="text"
                                            placeholder={`Enter question text (${lang.label})`}
                                            value={question.text[lang.value] || ""}
                                        />
                                    </Col>
                                ))}

                                {/* Answer type */}
                                <Col md={3}>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Answer Type</Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={question.type}
                                        onChange={(e) => updateQuestion(e, index)}
                                    >
                                        <option value="">Select Type</option>
                                        {QUESTION_TYPES.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                {/* Optional toggle */}
                                <Col md={2} className="d-flex align-items-center" style={{ marginTop: 32 }}>
                                    <Form.Check
                                        type="switch"
                                        id={`optional-switch-${index}`}
                                        name="optional"
                                        label="Optional"
                                        checked={question.optional || false}
                                        onChange={(e) => {
                                            const updatedQuestions = [...questions];
                                            updatedQuestions[index].optional = e.target.checked;
                                            setQuestions(updatedQuestions);
                                        }}
                                        style={{ color: Colors.PRM_LIGHT_BLUE }}
                                    />
                                </Col>

                                {/* Stars count for rating type */}
                                {question.type === 'rating' && (
                                    <Col md={3}>
                                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                                            Number of Stars
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max="10"
                                            placeholder="e.g., 5"
                                            value={question.starsCount || ''}
                                            onChange={(e) => {
                                                const updatedQuestions = [...questions];
                                                const count = parseInt(e.target.value) || 0;
                                                updatedQuestions[index].starsCount = count > 10 ? 10 : count;
                                                setQuestions(updatedQuestions);
                                            }}
                                        />
                                        <small className="text-muted">Max: 10 stars</small>
                                    </Col>
                                )}

                                {/* Answer options for each selected language */}
                                {typeNeedsOptions(question.type) &&
                                    selectedLanguages.map((lang) => (
                                        <Col md={3} key={lang.value}>
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                                                Options ({lang.label})
                                            </Form.Label>
                                            <ListGroup>
                                                {(question.options[lang.value] || []).map((opt, optIndex) => (
                                                    <Col className="d-flex">
                                                        <ListGroup.Item key={optIndex} className="d-flex p-1 mb-2">
                                                            <Form.Control
                                                                type={question.type === "rating" ? "number" : "text"}
                                                                value={opt}
                                                                onChange={(e) => handleEditOption(index, lang.value, optIndex, e.target.value)}
                                                            />
                                                        </ListGroup.Item>
                                                        <IoMdClose
                                                            onClick={() => handleDeleteOption(index, lang.value, optIndex)}
                                                            color={"#de1b1b"}
                                                            size={28}
                                                            style={{ cursor: "pointer", border: "1px solid #de1b1b", borderRadius: "6px", padding: "3px", marginInlineStart: "5px" }} />
                                                    </Col>

                                                ))}
                                            </ListGroup>
                                            <InputGroup className="mb-3">
                                                <Form.Control
                                                    type={question.type === "rating" ? "number" : "text"}
                                                    className="mr-3"
                                                    placeholder={`Add an option (${lang.label})`}
                                                    name={`option-${lang.value}`}
                                                    value={option[lang.value] || ""}
                                                    onChange={(e) =>
                                                        setOption((prev) => ({
                                                            ...prev,
                                                            [lang.value]: e.target.value,
                                                        }))
                                                    }
                                                />
                                                <FiPlus
                                                    onClick={() => handleAddOption(index, lang.value)}
                                                    color={Colors.PRM_LIGHT_BLUE}
                                                    size={25}
                                                    style={{ cursor: "pointer", border: `1px solid ${Colors.PRM_LIGHT_BLUE}`, borderRadius: "6px", padding: "3px", marginInlineStart: "5px" }}
                                                />
                                            </InputGroup>
                                        </Col>
                                    ))}
                                {question.type === 'matrix-scale5' ? (
                                    <>
                                        {/* Scale Labels Section */}
                                        <Col md={12} className="mb-3">
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>
                                                Scale Labels (1-5)
                                            </Form.Label>
                                            <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 12 }}>
                                                {selectedLanguages.map((lang) => (
                                                    <div key={lang.value} className="mb-3">
                                                        <Form.Label style={{ fontSize: 12, fontWeight: 500 }}>
                                                            {lang.label}
                                                        </Form.Label>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            {[0, 1, 2, 3, 4].map((labelIndex) => (
                                                                <div key={labelIndex} style={{ flex: 1 }}>
                                                                    <Form.Control
                                                                        size="sm"
                                                                        placeholder={`${labelIndex + 1}`}
                                                                        value={question.scaleLabels?.[lang.value]?.[labelIndex] || ''}
                                                                        onChange={(e) => handleEditScaleLabel(index, lang.value, labelIndex, e.target.value)}
                                                                        style={{ textAlign: 'center' }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                                <small className="text-muted">
                                                    Enter labels for each scale point (e.g., "Very Poor", "Poor", "Average", "Good", "Excellent")
                                                </small>
                                            </div>
                                        </Col>

                                        {/* Matrix Rows Section */}
                                        <Col md={12}>
                                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Matrix Rows (sub-questions)</Form.Label>

                                            <div>
                                            {question.items?.map((item, itemIndex) => (
                                                <div key={itemIndex} className="mb-2 p-2" style={{ border: '1px solid #eee', borderRadius: 6 }}>
                                                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                                        <div style={{ flex: 1 }}>
                                                            {selectedLanguages.map((lang) => (
                                                                <div key={lang.value}>
                                                                    <Form.Label style={{ fontSize: 12 }}>{`Row text (${lang.label})`}</Form.Label>
                                                                    <Form.Control
                                                                        value={item.text?.[lang.value] || ''}
                                                                        onChange={(e) => handleEditMatrixItem(index, itemIndex, lang.value, e.target.value)}
                                                                        placeholder={`Sub-question (${lang.label})`}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* delete button for the row */}
                                                        <div style={{ alignSelf: 'center' }}>
                                                            <button type="button" onClick={() => handleDeleteMatrixItem(index, itemIndex)} className="btn btn-sm btn-outline-danger">
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            <div style={{ marginTop: 6 }}>
                                                <button type="button" onClick={() => handleAddMatrixItem(index)} className="btn btn-sm btn-outline-primary">
                                                    + Add Row
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                    </>
                                ) : null}

                            </Row>
                        </Accordion>
                    </Col>
                    {/* remove question */}
                    {fullQuestionsArr?.length > 1 && <Col md={1}>
                        <MdClear
                            onClick={() => handleDeleteQuestion(index)}
                            color={"#de1b1b"}
                            size={28}
                            style={{ cursor: "pointer", border: "1px solid #de1b1b", borderRadius: "15px", padding: "3px" }}
                        />
                    </Col>}
                </Row>
            ))}
        </div>
    );
};


export default QuestionForm;
